'use client'

import { useActionState } from 'react'
import { subscribeFormAction, type SubscribeState } from '@/lib/newsletterActions'

/**
 * Il campo email e il pulsante. Client component per una ragione sola: mostrare l'esito senza
 * ricaricare la pagina.
 *
 * L'action è passata a `<form action={...}>` e non a un `onSubmit`, quindi il form FUNZIONA ANCHE
 * SENZA JAVASCRIPT: Next fa la POST nativa e la pagina torna con l'esito. Con JS attivo, la stessa
 * action gira in fetch e la pagina non si muove. Un `onSubmit` avrebbe rotto il primo caso.
 *
 * `pending` viene da useActionState: è la guardia contro il doppio invio (il pulsante si disabilita
 * per la durata della richiesta), senza il ref sincrono che serve invece nei fetch scritti a mano.
 */
export function NewsletterForm({ source, privacyUrl }: { source: 'footer' | 'pagina' | 'articolo'; privacyUrl?: string }) {
  const [state, formAction, pending] = useActionState<SubscribeState | null, FormData>(subscribeFormAction, null)

  const done = state?.status === 'ok'

  return (
    <div>
      <form action={formAction} className="flex flex-col gap-2.5 sm:flex-row">
        <input type="hidden" name="source" value={source} />
        {/* Campo esca: invisibile a chi legge, compilato dai bot che riempiono tutto. */}
        <div className="hidden" aria-hidden="true">
          <label htmlFor={`nl-website-${source}`}>Sito web</label>
          <input id={`nl-website-${source}`} type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue="" />
        </div>

        <label htmlFor={`nl-email-${source}`} className="sr-only">
          Il tuo indirizzo email
        </label>
        <input
          id={`nl-email-${source}`}
          type="email"
          name="email"
          required
          autoComplete="email"
          spellCheck={false}
          placeholder="nome@esempio.it"
          disabled={pending || done}
          aria-describedby={`nl-msg-${source}`}
          className="w-full rounded-full border border-zinc-200 bg-white px-5 py-3 text-base shadow-sm outline-none transition focus:border-accent focus:ring-4 focus:ring-accent/15 disabled:opacity-60 sm:flex-1"
        />
        <button
          type="submit"
          disabled={pending || done}
          className="rounded-full bg-accent px-5 py-3 text-sm font-semibold text-accent-contrast transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {pending ? 'Invio in corso' : 'Iscrivimi'}
        </button>
      </form>

      {/* L'esito si annuncia agli screen reader appena arriva, senza spostare il focus. */}
      <p
        id={`nl-msg-${source}`}
        aria-live="polite"
        className={`mt-2.5 text-sm ${
          state ? (done ? 'text-emerald-700' : 'text-red-700') : 'text-zinc-500'
        }`}
      >
        {state ? (
          state.message
        ) : privacyUrl ? (
          <>
            Niente pubblicità, disiscrizione con un clic.{' '}
            <a href={privacyUrl} className="underline decoration-zinc-300 underline-offset-2 hover:text-accent-strong">
              Come trattiamo i tuoi dati
            </a>
          </>
        ) : (
          'Niente pubblicità, disiscrizione con un clic.'
        )}
      </p>
    </div>
  )
}
